import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TICKET_STATES } from '@esa/shared';
import { ApiError, api } from '../lib/api.js';
import { Button, Input, Select, useToast } from '../ui/index.js';

/**
 * What to do with the tickets ticked in the queue (FR-AG-05): assign them,
 * move them to a state, or tag them — all at once. Each ticket is done on
 * its own, so one that cannot move (a missing field, a state it cannot go
 * to from where it is) is reported without holding up the rest.
 */

const STATE_LABEL: Record<string, string> = {
  new: 'New',
  open: 'Open',
  pending_requester: 'Waiting on requester',
  pending_external: 'Waiting on someone else',
  pending_internal: 'Waiting internally',
  pending_event: 'Waiting for an event',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};
/** States that need a reason on the way in. */
const NEEDS_REASON = new Set(['pending_requester', 'pending_external', 'pending_internal', 'resolved', 'cancelled']);

type Result = { number: number; ok: boolean; error?: string }[];

export function BulkBar({
  tenant,
  numbers,
  meOid,
  onClear,
  queues = [],
}: {
  tenant: string;
  numbers: number[];
  meOid: string | undefined;
  onClear: () => void;
  /** The queues to move to. Empty (the default) hides the control. */
  queues?: { key: string; name: string; restricted?: boolean }[];
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [state, setState] = useState('');
  const [reason, setReason] = useState('');
  const [tag, setTag] = useState('');
  const [queueKey, setQueueKey] = useState('');

  const run = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.post<{ results: Result } | Result>(`/api/v1/tenants/${tenant}/tickets/bulk`, { numbers, ...body }),
    onSuccess: (r) => {
      const results = Array.isArray(r) ? r : r.results;
      const failed = results.filter((x) => !x.ok);
      const done = results.length - failed.length;
      if (failed.length === 0) toast.success(`${done} ticket${done === 1 ? '' : 's'} changed`);
      else
        toast.error(
          `${done} changed, ${failed.length} could not be`,
          failed
            .slice(0, 5)
            .map((f) => `#${f.number}: ${f.error ?? 'failed'}`)
            .join(' · '),
        );
      void qc.invalidateQueries({ queryKey: ['tickets'] });
      void qc.invalidateQueries({ queryKey: ['queue-counts'] });
      onClear();
      setState('');
      setReason('');
      setTag('');
      setQueueKey('');
    },
    onError: (e) => toast.error('Nothing changed', e instanceof ApiError ? e.message : 'the change failed'),
  });

  const needsReason = NEEDS_REASON.has(state);
  // A restricted queue (072) hides its tickets from everyone else, possibly
  // including whoever is moving them: said before the button, as the ticket
  // page and the right-click menu do.
  const restrictedTarget = queues.find((q) => q.key === queueKey && q.restricted);
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-line bg-brand-tint/40 px-5 py-2 text-xs">
      <span className="font-medium text-ink">{numbers.length} selected</span>
      <Button size="sm" variant="secondary" disabled={!meOid || run.isPending} onClick={() => run.mutate({ action: 'assign', assigneeOid: meOid })}>
        Assign to me
      </Button>
      <Button size="sm" variant="ghost" disabled={run.isPending} onClick={() => run.mutate({ action: 'assign' })}>
        Unassign
      </Button>
      <span className="mx-1 h-4 w-px bg-line" aria-hidden />
      <div className="w-44">
        <Select
          value={state}
          onValueChange={setState}
          options={TICKET_STATES.filter((s) => s !== 'new' && s !== 'pending_event').map((s) => ({ value: s, label: STATE_LABEL[s] ?? s }))}
          placeholder="Change state to…"
          aria-label="Change state to"
        />
      </div>
      {state && needsReason && (
        <div className="w-56">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason" aria-label="Reason" />
        </div>
      )}
      {state && (
        <Button
          size="sm"
          disabled={run.isPending || (needsReason && reason.trim() === '')}
          onClick={() => run.mutate({ action: 'state', state, reason: reason.trim() || undefined })}
        >
          Apply
        </Button>
      )}
      {/* Clearing Triage is a bulk job: the tickets intake could not route. */}
      {queues.length > 1 && (
        <>
          <span className="mx-1 h-4 w-px bg-line" aria-hidden />
          <div className="w-44">
            <Select
              value={queueKey}
              onValueChange={setQueueKey}
              options={queues.map((q) => ({ value: q.key, label: q.restricted ? `${q.name} (restricted)` : q.name }))}
              placeholder="Move to queue…"
              aria-label="Move to queue"
            />
          </div>
          {restrictedTarget && (
            <span className="text-warn">
              Only members of {restrictedTarget.name} will see {numbers.length === 1 ? 'it' : 'them'}. If you are not
              one, {numbers.length === 1 ? 'it leaves' : 'they leave'} your view.
            </span>
          )}
          {queueKey && (
            <Button size="sm" disabled={run.isPending} onClick={() => run.mutate({ action: 'queue', queueKey })}>
              {restrictedTarget ? 'Move anyway' : 'Move'}
            </Button>
          )}
        </>
      )}
      <span className="mx-1 h-4 w-px bg-line" aria-hidden />
      <div className="w-36">
        <Input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="Add a tag" aria-label="Tag" />
      </div>
      {tag.trim() && (
        <Button size="sm" variant="secondary" disabled={run.isPending} onClick={() => run.mutate({ action: 'tag', tag: tag.trim() })}>
          Tag
        </Button>
      )}
      <Button size="sm" variant="ghost" className="ml-auto" onClick={onClear}>
        Clear
      </Button>
    </div>
  );
}
